<script setup lang="ts">
    import { ChevronRight } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogTrigger,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogDescription,
        DialogClose,
        DialogFooter,
        DialogBody,
    } from '@/components/ui/dialog';
    import { Alert } from '@/components/ui/alert';
    import { Checkbox } from '@/components/ui/checkbox';
    import { computed, ref } from 'vue';
    import { Label } from '@/components/ui/label';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import { useQueryClient } from '@tanstack/vue-query';
    import axios from 'axios';
    import QueryKey from '@/enums/QueryKey';
    import type { Voucher } from '@/types';

    const queryClient = useQueryClient();

    const props = defineProps<{
        voucher: Voucher;
    }>();

    const { t } = useI18n();

    const acceptAgreement = ref(false);
    const isOpen = ref(false);

    const isOverLimit = computed(() => {
        const currentSum = props.voucher.no_voucher ? Math.abs(props.voucher.Amount) : 0;

        if (!props.voucher.voucher_limit) return false;

        return currentSum > props.voucher.voucher_limit;
    });

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: { id: number }) =>
            axios.post(route('v2.voucher.upload.specific'), formData),
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: [QueryKey.VOUCHERS, props.voucher.company_id],
            });

            isOpen.value = false;
            toast.success(data.data.success);
        },
        onError: (error: any) => {
            isOpen.value = false;
            toast.error(error.response.data.message || t('forms.system_error'));
        },
    });

    function approve() {
        mutate({
            id: props.voucher.id,
        });
    }

    function handleCancel() {
        isOpen.value = false;
        acceptAgreement.value = false;
    }
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogTrigger as-child>
            <Button
                variant="accent"
                size="xs"
            >
                {{ $t('forms.approve') }}
                <ChevronRight />
            </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle> {{ t('vouchers.js.approve_title') }} {{ voucher.Text }} </DialogTitle>
            </DialogHeader>

            <DialogBody class="space-y-5">
                <DialogDescription>
                    {{ t('vouchers.js.approve_content') }}
                </DialogDescription>
                <div
                    v-if="isOverLimit"
                    class="space-y-3"
                >
                    <Alert variant="warning">
                        {{ t('vouchers.js.limit_amount') }} {{ voucher.voucher_limit }}
                    </Alert>
                    <Label
                        for="acceptAgreement"
                        class="flex cursor-pointer items-center"
                    >
                        <Checkbox
                            v-model="acceptAgreement"
                            id="acceptAgreement"
                        />
                        <span>{{ $t('vouchers.js.limit_agreement') }}</span>
                    </Label>
                </div>
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="handleCancel"
                    >
                        {{ $t('forms.cancel') }}
                    </Button>
                </DialogClose>

                <Button
                    :disabled="isPending || (isOverLimit && !acceptAgreement)"
                    :loading="isPending"
                    size="sm"
                    @click="approve"
                >
                    {{ $t('forms.yes') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
